import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1515987913882-11f2c427c484?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwbWVuc3dlYXIlMjBlZGl0b3JpYWwlMjBzdHJlZXR3ZWFyJTIwbmF2eXxlbnwwfHx8Ymx1ZXwxNzkxNTc0ODAxfDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

export const HeroKinetic = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-28"
    >
      <div className="pointer-events-none absolute -right-10 top-24 hidden select-none font-display text-[26rem] font-bold italic leading-none text-stroke opacity-40 lg:block">
        V
      </div>
      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-5 md:grid-cols-12 md:px-8 lg:gap-8">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-12 bg-volt" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-volt" data-testid="hero-kicker">
              {t.hero.kicker}
            </span>
          </motion.div>
          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === t.hero.lines.length - 1 ? "italic text-volt" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-md text-sm leading-relaxed text-mutedink sm:text-base"
            data-testid="hero-subline"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToId("katalog")}
              className="group flex items-center gap-2 bg-volt px-7 py-4 font-grotesk text-sm font-bold uppercase tracking-widest text-obsidian transition-colors duration-300 hover:bg-volt-hover"
            >
              {t.hero.cta}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-secondary-button"
              onClick={() => scrollToId("so-gehts")}
              className="border border-ink/25 px-7 py-4 font-grotesk text-sm font-medium uppercase tracking-widest text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-obsidian"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 grid max-w-md grid-cols-3 divide-x divide-edge border-y border-edge"
            data-testid="hero-stats"
          >
            {t.hero.stats.map(([num, label], i) => (
              <div key={i} className="px-4 py-4 first:pl-0">
                <div className="font-mono text-xl font-bold text-volt">{num}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-mutedink">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="md:col-span-5" style={{ perspective: 1200 }} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
          >
            <div className="relative rotate-[0.5deg] border border-edge bg-surface p-3 shadow-[0_2px_16px_rgba(18,20,23,0.08)]">
              <span className="absolute -left-px -top-px z-10 h-6 w-6 border-l-2 border-t-2 border-volt" />
              <span className="absolute -bottom-px -right-px z-10 h-6 w-6 border-b-2 border-r-2 border-volt" />
              <div className="overflow-hidden">
                <motion.img
                  src={HERO_IMG}
                  alt="Vintage menswear editorial"
                  data-testid="hero-image"
                  style={{ y: imgY }}
                  className="h-[380px] w-full scale-110 object-cover sm:h-[440px] lg:h-[540px]"
                />
              </div>
              <div className="absolute bottom-7 left-7 border border-edge bg-surface/90 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink backdrop-blur-sm">
                VNT — ARCHIVE/001
              </div>
              <div className="absolute right-7 top-7 bg-volt px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-obsidian">
                Neu // Vinted
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("katalog")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-mutedink transition-colors hover:text-volt md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
};
